import React, { useState, useEffect } from "react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
} from "recharts";
import { customFetch } from '../apiClient';

const COLORS = ["#0ea5e9", "#06b6d4", "#14b8a6", "#3b82f6", "#6366f1"];

const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const res = await customFetch('/Dashboard/summary');
        if (res.ok) {
          const result = await res.json();
          setData(result);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardStats();
    const refresh = setInterval(fetchDashboardStats, 120000);
    return () => clearInterval(refresh);
  }, []);

  const greeting = () => {
    const h = currentTime.getHours();
    if (h < 12) return "Good Morning";
    if (h < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const formatTime = () =>
    currentTime.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });

  const formatDate = () =>
    currentTime.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  if (loading) return (
    <div className="dash-loading">
      <div className="dash-spinner"></div>
      <h4>Loading Real-Time Dashboard…</h4>
    </div>
  );
  if (!data) return (
    <div className="dash-error">
      <i className="bi bi-exclamation-triangle-fill"></i>
      <h4>Failed to load data</h4>
      <p>Please check your connection and try again.</p>
    </div>
  );

  const totalBeds = data.totalBeds || 0;
  const occupied = data.occupiedBeds || 0;
  const occPct = totalBeds ? Math.round((occupied / totalBeds) * 100) : 0;

  return (
    <div className="dashboard-container">
      {/* ===== Dark Hero with overlapping cards ===== */}
      <div className="hero">
        <div className="hero-bg-grid"></div>
        <div className="hero-glow hero-glow-1"></div>
        <div className="hero-glow hero-glow-2"></div>

        <div className="hero-top">
          <div className="hero-greet">
            <div className="hero-eyebrow"><span className="live-dot"></span> LIVE DASHBOARD</div>
            <h1 className="hero-title">{greeting()}, Admin</h1>
            <p className="hero-sub">Here's what's happening across the hospital today</p>
          </div>
          <div className="hero-clock">
            <div className="clock-time">{formatTime()}</div>
            <div className="clock-date">{formatDate()}</div>
          </div>
        </div>
      </div>

      {/* ===== Floating Summary Cards (overlap hero) ===== */}
      <div className="float-cards">
        <div className="fcard">
          <div className="fcard-top">
            <div className="fcard-icon ic-blue"><i className="bi bi-people-fill"></i></div>
            <span className="fcard-tag tag-blue">Live</span>
          </div>
          <div className="fcard-value">{data.totalPatients || 0}</div>
          <div className="fcard-label">Patients This Month</div>
        </div>

        <div className="fcard">
          <div className="fcard-top">
            <div className="fcard-icon ic-cyan"><i className="bi bi-calendar-check-fill"></i></div>
            <span className="fcard-tag tag-cyan">OPD</span>
          </div>
          <div className="fcard-value">{data.todayOPD || 0}</div>
          <div className="fcard-label">Today's Outpatients</div>
        </div>

        <div className="fcard">
          <div className="fcard-top">
            <div className="fcard-icon ic-teal"><i className="bi bi-hospital-fill"></i></div>
            <span className="fcard-tag tag-teal">IPD</span>
          </div>
          <div className="fcard-value">{data.todayIPD || 0}</div>
          <div className="fcard-label">Today's Admissions</div>
        </div>

        <div className="fcard">
          <div className="fcard-top">
            <div className="fcard-icon ic-green"><i className="bi bi-currency-rupee"></i></div>
            <span className="fcard-tag tag-green">Revenue</span>
          </div>
          <div className="fcard-value">₹{data.todayRevenue?.toLocaleString('en-IN') || 0}</div>
          <div className="fcard-label">Collected Today</div>
        </div>
      </div>

      {/* ===== Bento Grid ===== */}
      <div className="bento">
        {/* Bed Occupancy — wide */}
        <div className="bento-card bento-beds">
          <div className="bento-head">
            <span><i className="bi bi-hospital"></i> Bed Occupancy</span>
            <span className="bento-pct">{occPct}%</span>
          </div>
          <div className="occ-bar">
            <div className="occ-fill" style={{ width: `${occPct}%` }}></div>
          </div>
          <div className="occ-stats">
            <div className="occ-stat">
              <div className="occ-num text-teal">{data.availableBeds || 0}</div>
              <div className="occ-cap">Available</div>
            </div>
            <div className="occ-stat">
              <div className="occ-num text-amber">{occupied}</div>
              <div className="occ-cap">Occupied</div>
            </div>
            <div className="occ-stat">
              <div className="occ-num">{totalBeds}</div>
              <div className="occ-cap">Total Beds</div>
            </div>
          </div>
        </div>

        {/* Pending Dues — accent */}
        <div className="bento-card bento-dues">
          <div className="dues-icon"><i className="bi bi-exclamation-circle-fill"></i></div>
          <div className="dues-label">Pending Dues</div>
          <div className="dues-value">₹{data.pendingDues?.toLocaleString('en-IN') || 0}</div>
          <div className="dues-foot"><i className="bi bi-arrow-right-circle"></i> Needs collection</div>
        </div>

        {/* Recent Registrations — wide */}
        <div className="bento-card bento-table">
          <div className="bento-head">
            <span><i className="bi bi-clock-history"></i> Recent Registrations</span>
          </div>
          <div className="table-body">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#ID</th>
                  <th>Patient Name</th>
                  <th>Doctor</th>
                  <th>Date</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {data.recentPatients && data.recentPatients.map((patient, idx) => (
                  <tr key={idx}>
                    <td data-label="#ID">{patient.id}</td>
                    <td data-label="Patient">
                      <div className="patient-name"><i className="bi bi-person-circle"></i> {patient.name}</div>
                    </td>
                    <td data-label="Doctor">{patient.doctor}</td>
                    <td data-label="Date">{patient.date}</td>
                    <td data-label="Type"><span className={`status-badge ${patient.type}`}>{patient.status}</span></td>
                  </tr>
                ))}
                {(!data.recentPatients || data.recentPatients.length === 0) && (
                  <tr><td colSpan="5" className="text-center">No recent patients found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Department Pie — narrow */}
        <div className="bento-card bento-pie">
          <div className="bento-head">
            <span><i className="bi bi-pie-chart-fill"></i> Top Departments</span>
          </div>
          {data.departmentData && data.departmentData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={data.departmentData} cx="50%" cy="50%" innerRadius={48} outerRadius={75} paddingAngle={4} dataKey="value">
                    {data.departmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="pie-legend">
                {data.departmentData.map((dept, idx) => (
                  <div key={idx} className="legend-item">
                    <span className="legend-color" style={{ background: COLORS[idx % COLORS.length] }}></span>
                    <span className="legend-label">{dept.name}</span>
                    <span className="legend-value">{dept.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-muted text-center mt-4">Not enough data to show chart.</p>
          )}
        </div>
      </div>

      <style>{`
        .dashboard-container { padding: 0; font-family: 'Inter', system-ui, -apple-system, sans-serif; }

        /* ===== Loading / Error ===== */
        .dash-loading, .dash-error {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; min-height: 60vh; gap: 14px; text-align: center;
        }
        .dash-loading h4, .dash-error h4 { color: #1e293b; font-weight: 700; margin: 0; }
        .dash-error i { font-size: 42px; color: #f59e0b; }
        .dash-error p { color: #64748b; font-size: 14px; margin: 0; }
        .dash-spinner {
          width: 44px; height: 44px; border-radius: 50%;
          border: 4px solid #e2e8f0; border-top-color: #0ea5e9;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* ===== Dark Hero ===== */
        .hero {
          position: relative;
          background: linear-gradient(125deg, #0c4a6e 0%, #0369a1 45%, #0891b2 100%);
          border-radius: 22px;
          padding: 30px 34px 70px;
          overflow: hidden;
          box-shadow: 0 14px 36px -10px rgba(8, 47, 73, 0.55);
        }
        .hero-bg-grid {
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(ellipse at top right, black, transparent 70%);
          -webkit-mask-image: radial-gradient(ellipse at top right, black, transparent 70%);
        }
        .hero-glow { position: absolute; border-radius: 50%; filter: blur(60px); opacity: 0.5; pointer-events: none; }
        .hero-glow-1 { width: 260px; height: 260px; background: #06b6d4; top: -100px; right: 4%; }
        .hero-glow-2 { width: 200px; height: 200px; background: #3b82f6; bottom: -80px; left: 12%; opacity: 0.35; }

        .hero-top {
          position: relative; z-index: 1;
          display: flex; justify-content: space-between; align-items: flex-start; gap: 20px;
        }
        .hero-eyebrow {
          display: inline-flex; align-items: center; gap: 7px;
          font-size: 10.5px; font-weight: 800; letter-spacing: 1px; color: #e0f2fe;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.2);
          padding: 4px 11px; border-radius: 20px; margin-bottom: 14px;
          backdrop-filter: blur(4px);
        }
        .live-dot {
          width: 7px; height: 7px; border-radius: 50%; background: #4ade80;
          box-shadow: 0 0 0 3px rgba(74,222,128,0.3); animation: hero-pulse 2s ease infinite;
        }
        @keyframes hero-pulse { 0%,100% { box-shadow: 0 0 0 3px rgba(74,222,128,0.3);} 50% { box-shadow: 0 0 0 6px rgba(74,222,128,0.1);} }
        .hero-title { margin: 0 0 6px; font-size: 28px; font-weight: 800; color: #fff; letter-spacing: -0.4px; text-shadow: 0 2px 14px rgba(0,0,0,0.2); }
        .hero-sub { margin: 0; font-size: 14px; color: rgba(255,255,255,0.82); font-weight: 500; }
        .hero-clock { text-align: right; flex-shrink: 0; }
        .clock-time { font-size: 30px; font-weight: 800; color: #fff; line-height: 1; font-family: 'Consolas','Monaco',monospace; letter-spacing: 0.5px; text-shadow: 0 2px 14px rgba(0,0,0,0.2); }
        .clock-date { font-size: 12.5px; color: rgba(255,255,255,0.82); margin-top: 6px; font-weight: 600; }

        /* ===== Floating cards (overlap hero) ===== */
        .float-cards {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;
          margin: -48px 14px 0; position: relative; z-index: 5;
        }
        .fcard {
          background: #fff; border-radius: 16px; padding: 18px;
          box-shadow: 0 10px 30px -10px rgba(15,23,42,0.2), 0 2px 8px rgba(15,23,42,0.06);
          border: 1px solid #eef2f7;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .fcard:hover { transform: translateY(-5px); box-shadow: 0 18px 38px -12px rgba(15,23,42,0.26); }
        .fcard-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
        .fcard-icon {
          width: 46px; height: 46px; border-radius: 13px;
          display: flex; align-items: center; justify-content: center;
          font-size: 21px; color: #fff;
        }
        .ic-blue { background: linear-gradient(135deg, #0ea5e9, #0284c7); box-shadow: 0 6px 14px -4px rgba(2,132,199,0.5); }
        .ic-cyan { background: linear-gradient(135deg, #06b6d4, #0891b2); box-shadow: 0 6px 14px -4px rgba(8,145,178,0.5); }
        .ic-teal { background: linear-gradient(135deg, #14b8a6, #0d9488); box-shadow: 0 6px 14px -4px rgba(13,148,136,0.5); }
        .ic-green { background: linear-gradient(135deg, #10b981, #059669); box-shadow: 0 6px 14px -4px rgba(5,150,105,0.5); }
        .fcard-tag { font-size: 10px; font-weight: 800; padding: 3px 9px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.4px; }
        .tag-blue { background: #e0f2fe; color: #0284c7; }
        .tag-cyan { background: #cffafe; color: #0891b2; }
        .tag-teal { background: #ccfbf1; color: #0d9488; }
        .tag-green { background: #dcfce7; color: #059669; }
        .fcard-value { font-size: 30px; font-weight: 800; color: #0f172a; line-height: 1; letter-spacing: -0.5px; margin-bottom: 6px; }
        .fcard-label { font-size: 12.5px; color: #64748b; font-weight: 600; }

        /* ===== Bento Grid ===== */
        .bento {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 18px; margin-top: 22px;
        }
        .bento-card {
          background: #fff; border-radius: 18px; padding: 20px;
          border: 1px solid #eef2f7;
          box-shadow: 0 1px 3px rgba(15,23,42,0.05), 0 4px 14px rgba(15,23,42,0.04);
        }
        .bento-head {
          display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;
          font-size: 15px; font-weight: 700; color: #0f172a;
        }
        .bento-head span:first-child { display: flex; align-items: center; gap: 9px; }
        .bento-head i { color: #0ea5e9; }

        .bento-beds { grid-column: span 2; }
        .bento-dues { grid-column: span 2; background: linear-gradient(135deg, #0c4a6e, #0e7490); color: #fff; border: none; display: flex; flex-direction: column; }
        .bento-table { grid-column: span 3; }
        .bento-pie { grid-column: span 1; }

        /* Bed occupancy */
        .bento-pct { font-size: 20px; font-weight: 800; color: #0891b2; }
        .occ-bar { height: 12px; background: #f1f5f9; border-radius: 8px; overflow: hidden; margin-bottom: 18px; }
        .occ-fill { height: 100%; border-radius: 8px; background: linear-gradient(90deg, #0ea5e9, #06b6d4); transition: width 1s ease; }
        .occ-stats { display: flex; gap: 12px; }
        .occ-stat { flex: 1; text-align: center; background: #f8fafc; border-radius: 12px; padding: 14px 8px; border: 1px solid #f1f5f9; }
        .occ-num { font-size: 24px; font-weight: 800; color: #0f172a; line-height: 1; }
        .occ-cap { font-size: 11px; color: #64748b; font-weight: 600; margin-top: 6px; text-transform: uppercase; letter-spacing: 0.3px; }
        .text-teal { color: #0d9488; }
        .text-amber { color: #d97706; }

        /* Dues card */
        .dues-icon { font-size: 26px; color: #67e8f9; margin-bottom: 10px; }
        .dues-label { font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.8); text-transform: uppercase; letter-spacing: 0.5px; }
        .dues-value { font-size: 34px; font-weight: 800; color: #fff; margin: 6px 0 12px; letter-spacing: -0.5px; }
        .dues-foot { font-size: 12.5px; color: rgba(255,255,255,0.7); font-weight: 600; display: flex; align-items: center; gap: 6px; margin-top: auto; }

        /* Table */
        .table-body { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 480px; }
        .data-table thead th {
          text-align: left; padding: 11px 12px; background: #f8fafc; color: #475569; font-weight: 700;
          border-bottom: 2px solid #e2e8f0; font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px; white-space: nowrap;
        }
        .data-table thead th:first-child { border-radius: 8px 0 0 8px; }
        .data-table thead th:last-child { border-radius: 0 8px 8px 0; }
        .data-table tbody td { padding: 13px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }
        .data-table tbody tr { transition: background 0.15s; }
        .data-table tbody tr:hover { background: #f8fafc; }
        .patient-name { display: flex; align-items: center; gap: 8px; font-weight: 600; color: #0f172a; }
        .patient-name i { font-size: 19px; color: #0ea5e9; }
        .status-badge { padding: 5px 11px; border-radius: 20px; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
        .status-badge.success { background: #dcfce7; color: #16a34a; }
        .status-badge.ipd, .status-badge.IPD { background: #ccfbf1; color: #0d9488; }
        .status-badge.opd, .status-badge.OPD { background: #e0f2fe; color: #0284c7; }

        /* Pie */
        .pie-legend { margin-top: 12px; }
        .legend-item { display: flex; align-items: center; gap: 10px; padding: 7px 0; font-size: 13px; border-bottom: 1px solid #f8fafc; }
        .legend-item:last-child { border-bottom: none; }
        .legend-color { width: 13px; height: 13px; border-radius: 4px; flex-shrink: 0; }
        .legend-label { flex: 1; color: #334155; font-weight: 600; }
        .legend-value { color: #64748b; font-weight: 700; }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 1100px) {
          .bento-table { grid-column: span 4; }
          .bento-pie { grid-column: span 4; }
        }
        @media (max-width: 900px) {
          .float-cards { grid-template-columns: repeat(2, 1fr); }
          .bento { grid-template-columns: repeat(2, 1fr); }
          .bento-beds, .bento-dues, .bento-table, .bento-pie { grid-column: span 2; }
        }
        @media (max-width: 768px) {
          .hero { padding: 24px 22px 64px; }
          .hero-top { flex-direction: column; }
          .hero-clock { text-align: left; }
          .hero-title { font-size: 23px; }
          .clock-time { font-size: 24px; }
        }
        @media (max-width: 600px) {
          .float-cards { grid-template-columns: 1fr; margin-top: -40px; }
          .bento { grid-template-columns: 1fr; }
          .bento-beds, .bento-dues, .bento-table, .bento-pie { grid-column: span 1; }
          .occ-stats { gap: 8px; }
          .occ-num { font-size: 20px; }

          /* table -> cards */
          .data-table { min-width: 0; }
          .data-table thead { display: none; }
          .data-table, .data-table tbody, .data-table tr, .data-table td { display: block; width: 100%; }
          .data-table tbody tr { margin-bottom: 12px; border: 1px solid #eef2f7; border-radius: 12px; padding: 6px 12px; }
          .data-table tbody td { display: flex; justify-content: space-between; align-items: center; padding: 9px 0; border-bottom: 1px solid #f8fafc; text-align: right; }
          .data-table tbody td:last-child { border-bottom: none; }
          .data-table tbody td::before { content: attr(data-label); font-weight: 700; color: #94a3b8; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; text-align: left; }
          .patient-name { justify-content: flex-end; }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;